import { ERROR_CODES } from '@hvac/core';
import { z } from 'zod';
/**
 * The response envelope.
 *
 * Every endpoint, without exception, returns one of two shapes:
 *
 *   { "ok": true,  "data": <payload>, "meta": { ... } }
 *   { "ok": false, "error": { "code", "message", "details" }, "meta": { ... } }
 *
 * A uniform envelope means the client has exactly one place that decides
 * "did this work?", and every failure automatically carries the request id a
 * user can quote to support.
 */
export const apiMetaSchema = z.object({
    /** Correlates this response with server logs and audit rows. */
    requestId: z.string().min(1),
    timestamp: z.iso.datetime(),
});
export const errorCodeSchema = z.enum(Object.values(ERROR_CODES));
export const apiErrorBodySchema = z.object({
    /** Stable machine code. Branch on this, never on `message`. */
    code: errorCodeSchema,
    /** Human-readable and safe to display. Already redacted server-side. */
    message: z.string().min(1),
    /** Structured context, e.g. per-field validation issues. */
    details: z.record(z.string(), z.unknown()).optional(),
});
export const apiFailureSchema = z.object({
    ok: z.literal(false),
    error: apiErrorBodySchema,
    meta: apiMetaSchema,
});
export function apiSuccessSchema(data) {
    return z.object({ ok: z.literal(true), data, meta: apiMetaSchema });
}
/** Full response schema for a given payload, used by the client to validate. */
export function apiResponseSchema(data) {
    return z.discriminatedUnion('ok', [apiSuccessSchema(data), apiFailureSchema]);
}
/**
 * Envelope shape with an unvalidated payload.
 *
 * Clients validate in two steps — envelope first, then payload — rather than
 * composing one generic schema. Two concrete schemas narrow cleanly on `ok`,
 * whereas a generically-parameterised union infers into an opaque mapped type
 * that TypeScript cannot discriminate. The runtime guarantee is identical and
 * the failure messages are more precise: "the envelope is malformed" and "the
 * payload does not match the contract" are genuinely different problems.
 */
export const apiEnvelopeSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), data: z.unknown(), meta: apiMetaSchema }),
    apiFailureSchema,
]);
/** Validation issue shape carried in `details.issues` of a 400 response. */
export const validationIssueSchema = z.object({
    path: z.string(),
    message: z.string(),
});
//# sourceMappingURL=envelope.js.map